import { useEffect, useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import axios from "axios";
import { path } from "../../../routes";

const CAMERA_OPTIONS = [
  { id: "cam1", key: "camera1", label: "Camera 1", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream1" },
  { id: "cam2", key: "camera2", label: "Camera 2", url: "rtsp://admin:xZSx2026xNbk%21@89.190.32.75:554/stream2" },
];

const normalizeRtspUrl = (u) => {
  const s = String(u || "").trim();
  if (!/^rtsp:\/\//i.test(s)) return s;
  const m = s.match(/^rtsp:\/\/([^@\/]+)@(.+)$/i);
  if (!m) return s;
  const userinfo = m[1];
  const hostPath = m[2];
  const idx = userinfo.indexOf(":");
  if (idx < 0) return s;
  const user = userinfo.slice(0, idx);
  const passRaw = userinfo.slice(idx + 1);
  let pass = passRaw;
  try { pass = decodeURIComponent(passRaw); } catch (_) {}
  const enc = (v) => encodeURIComponent(v).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  return `rtsp://${enc(user)}:${enc(pass)}@${hostPath}`;
};

const LiveCameras = ({ cred }) => {
  const [devices, setDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(CAMERA_OPTIONS[0].id);
  // Per-device persistent settings { [deviceId]: { liveCameraEnabled, liveCameraKey, ... } }
  // Drží stav z DB; checkboxy a "disabled" UI z toho čtou. Při Save jen tahle
  // mapa se přepíše pro aktuálně vybranou kameru.
  const [deviceSettingsMap, setDeviceSettingsMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [cameraUrl, setCameraUrl] = useState(CAMERA_OPTIONS[0].url);
  // Zdroj streamu pro vybrané displeje:
  //  - "auto"   : webOS hraje přímo z kamery, ostatní přes server (výchozí)
  //  - "direct" : vynutit přímý RTSP v <video> (LG webOS, odlehčí server)
  //  - "server" : natvrdo přes serverový ffmpeg HLS most
  const [sourceMode, setSourceMode] = useState("auto");

  const cameraById = useMemo(
    () => Object.fromEntries(CAMERA_OPTIONS.map((c) => [c.id, c])),
    []
  );

  // Která kamera je k devicu přiřazená (key, např. "camera2") nebo null.
  const deviceCameraKey = (deviceId) => {
    const s = deviceSettingsMap[deviceId];
    if (!s?.liveCameraEnabled) return null;
    return (typeof s.liveCameraKey === "string" && s.liveCameraKey.trim())
      ? s.liveCameraKey.trim()
      : null;
  };

  const selectedCameraKey = cameraById[selectedCamera]?.key;
  const selectedDeviceIds = devices
    .filter((d) => deviceCameraKey(d.id) === selectedCameraKey)
    .map((d) => d.id);

  const getDeviceSettingsSafe = async (deviceId, orgId) => {
    try {
      const resp = await axios.get(`${path}/getDeviceSettings/${deviceId}?orgID=${encodeURIComponent(orgId)}`);
      return (resp.data?.settings && typeof resp.data.settings === "object") ? resp.data.settings : {};
    } catch (_) {
      return {};
    }
  };

  const fetchDevices = async () => {
    try {
      const orgId = cred?.cOrganization_id;
      const [kiosksRes, displaysRes] = await Promise.all([
        axios.get(`${path}/getDeviceV2?orgID=${orgId}`).catch(() => ({ data: [] })),
        axios.get(`${path}/getDisplaysbyOrg/${orgId}`).catch(() => ({ data: [] })),
      ]);
      const kiosks = Array.isArray(kiosksRes.data) ? kiosksRes.data : [];
      const displays = Array.isArray(displaysRes.data) ? displaysRes.data : [];
      const seen = new Set();
      const all = [...displays, ...kiosks].filter((d) => {
        const id = Number(d?.id);
        if (!Number.isFinite(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setDevices(all);

      setLoadingAssignments(true);
      const map = {};
      let firstEnabledCameraId = null;
      let firstEnabledUrl = null;
      let firstEnabledMode = null;
      for (const d of all) {
        // eslint-disable-next-line no-await-in-loop
        const s = await getDeviceSettingsSafe(d.id, orgId);
        map[d.id] = s;
        if (s?.liveCameraEnabled) {
          if (!firstEnabledCameraId && typeof s?.liveCameraKey === "string" && s.liveCameraKey.trim()) {
            const mapped = CAMERA_OPTIONS.find((c) => c.key === s.liveCameraKey.trim());
            if (mapped) firstEnabledCameraId = mapped.id;
          }
          if (!firstEnabledUrl && typeof s?.liveCameraUrl === "string" && s.liveCameraUrl.trim()) {
            firstEnabledUrl = s.liveCameraUrl.trim();
          }
          if (!firstEnabledMode) {
            firstEnabledMode = s?.liveCameraForceServer === true
              ? "server"
              : (s?.liveCameraDirectRtsp === true ? "direct" : "auto");
          }
        }
      }
      setDeviceSettingsMap(map);
      if (firstEnabledCameraId) setSelectedCamera(firstEnabledCameraId);
      if (firstEnabledUrl) setCameraUrl(firstEnabledUrl);
      if (firstEnabledMode) setSourceMode(firstEnabledMode);
    } catch (e) {
      console.error("Failed to load devices for live cameras:", e);
      setDevices([]);
      setDeviceSettingsMap({});
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cred?.cOrganization_id]);

  // Toggle: jen pro devicy které nepatří k jiné kameře (jinak je v UI disabled).
  const toggleDevice = (id) => {
    const otherCamKey = deviceCameraKey(id);
    if (otherCamKey && otherCamKey !== selectedCameraKey) return; // assigned to other camera → no-op
    setDeviceSettingsMap((prev) => {
      const cur = prev[id] || {};
      const isOn = cur?.liveCameraEnabled && cur?.liveCameraKey === selectedCameraKey;
      const camera = cameraById[selectedCamera];
      return {
        ...prev,
        [id]: {
          ...cur,
          liveCameraEnabled: !isOn,
          liveCameraKey: !isOn ? selectedCameraKey : null,
          liveCameraName: !isOn ? (camera?.label || null) : null,
          liveCameraUrl: !isOn ? (cameraUrl || camera?.url || null) : null,
        },
      };
    });
  };

  useEffect(() => {
    const camera = cameraById[selectedCamera];
    if (!camera) return;
    // Preferuj URL přiřazenou už uloženým devicům pro tuhle kameru, jinak default šablona.
    const existingUrl = devices
      .map((d) => deviceSettingsMap[d.id])
      .find((s) => s?.liveCameraEnabled && s?.liveCameraKey === camera.key && s?.liveCameraUrl)?.liveCameraUrl;
    setCameraUrl(existingUrl || camera.url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCamera]);

  const save = async () => {
    const camera = cameraById[selectedCamera];
    if (!camera) return;
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      const targetUrl = normalizeRtspUrl(String(cameraUrl || camera.url).trim());
      const isRtsp = /^rtsp:\/\//i.test(targetUrl);
      // Save sequentially to avoid overloading backend (large Promise.all caused freezes).
      for (const d of devices) {
        const cur = deviceSettingsMap[d.id] || {};
        const otherCamKey = deviceCameraKey(d.id);
        // Devicy patřící k JINÉ kameře necháváme být.
        if (otherCamKey && otherCamKey !== camera.key) continue;
        const isSelected = selectedDeviceIds.includes(d.id);
        const wasEnabled = Boolean(cur?.liveCameraEnabled);
        const hadCameraData = Boolean(cur?.liveCameraUrl || cur?.liveCameraKey);
        if (!isSelected && !wasEnabled && !hadCameraData) continue;
        const next = {
          ...cur,
          liveCameraEnabled: Boolean(isSelected),
          liveCameraUrl: isSelected ? targetUrl : null,
          liveCameraName: isSelected ? camera.label : null,
          liveCameraKey: isSelected ? camera.key : null,
          // Zdroj streamu pro tento displej (jen když je vybraný).
          liveCameraDirectRtsp: isSelected ? (sourceMode === "direct") : false,
          liveCameraForceServer: isSelected ? (sourceMode === "server") : false,
        };
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }
      // For RTSP source, ask backend to (re)start live bridge so kiosk gets fresh live stream.
      if (isRtsp && selectedDeviceIds.length > 0) {
        await axios.post(`${path}/live-camera/start`, { cameraKey: camera.key, rtspUrl: targetUrl }).catch(() => {});
      }
      alert("Live camera assignment saved.");
      // Refresh, ať UI ukazuje aktuální stav z DB.
      await fetchDevices();
    } catch (e) {
      console.error("Failed to save live camera assignment:", e);
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const disableAll = async () => {
    setSaving(true);
    try {
      const orgId = cred?.cOrganization_id;
      for (const d of devices) {
        // eslint-disable-next-line no-await-in-loop
        const current = await getDeviceSettingsSafe(d.id, orgId);
        const next = {
          ...current,
          liveCameraEnabled: false,
          liveCameraUrl: null,
          liveCameraName: null,
          liveCameraKey: null,
          liveCameraDirectRtsp: false,
          liveCameraForceServer: false,
        };
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${path}/setDeviceSettings/${d.id}`, { settings: next, orgID: orgId });
      }
      await Promise.allSettled([
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera1" }),
        axios.post(`${path}/live-camera/stop`, { cameraKey: "camera2" }),
      ]);
      alert("Live cameras disabled on all devices.");
      await fetchDevices();
    } catch (e) {
      console.error("Failed to disable live cameras:", e);
      alert("Disable failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2 },
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
        Live Cameras
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "#666", lineHeight: 1.5 }}>
        Select camera and devices where live stream should be shown. Save to activate, Disable All to stop.
      </Typography>
      <FormControl fullWidth sx={{ mb: 2, minWidth: 0, maxWidth: { xs: "100%", sm: 480, md: 560 } }}>
        <InputLabel id="live-camera-label">Camera</InputLabel>
        <Select
          labelId="live-camera-label"
          label="Camera"
          value={selectedCamera}
          onChange={(e) => setSelectedCamera(e.target.value)}
          renderValue={(id) => {
            const c = cameraById[id];
            return c ? c.label : "";
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxWidth: "calc(100vw - 32px)" },
            },
          }}
        >
          {CAMERA_OPTIONS.map((c) => (
            <MenuItem
              key={c.id}
              value={c.id}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                alignItems: "flex-start",
                py: 1,
              }}
            >
              <Typography component="span" variant="body2" sx={{ display: "block" }}>
                <strong>{c.label}</strong>
                <br />
                <span style={{ opacity: 0.85 }}>{c.url}</span>
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Stream URL (HLS/WebRTC preferred)"
        value={cameraUrl}
        onChange={(e) => setCameraUrl(e.target.value)}
        sx={{
          mb: 2,
          maxWidth: { xs: "100%", md: 900 },
          "& .MuiInputBase-input": {
            fontSize: { xs: "0.875rem", sm: "1rem" },
            wordBreak: "break-all",
          },
        }}
        multiline
        minRows={2}
        helperText="Use external URL (e.g. https://.../index.m3u8). RTSP works only with server transcoding enabled."
      />
      <FormControl fullWidth sx={{ mb: 2, minWidth: 0, maxWidth: { xs: "100%", sm: 480, md: 560 } }}>
        <InputLabel id="live-source-label">Stream source</InputLabel>
        <Select
          labelId="live-source-label"
          label="Stream source"
          value={sourceMode}
          onChange={(e) => setSourceMode(e.target.value)}
        >
          <MenuItem value="auto">Auto (webOS přímo, ostatní přes server)</MenuItem>
          <MenuItem value="direct">Přímý RTSP (LG webOS – odlehčí server)</MenuItem>
          <MenuItem value="server">Přes server (ffmpeg HLS most)</MenuItem>
        </Select>
        <Typography variant="caption" sx={{ mt: 0.5, color: "#888", lineHeight: 1.4 }}>
          {sourceMode === "direct"
            ? "Stream jde přímo z kamery do <video> na displeji. Pokud nenaběhne, samo spadne na server."
            : sourceMode === "server"
            ? "Vždy přes serverový ffmpeg most. Přímý RTSP se ani nezkusí."
            : "webOS displeje hrají RTSP přímo, ostatní automaticky přes server."}
        </Typography>
      </FormControl>
      <Box
        sx={{
          maxHeight: { xs: 280, sm: 420 },
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: { xs: 1, sm: 1.5 },
          mb: 2,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {loadingAssignments ? (
          <Typography variant="body2" color="text.secondary">Loading saved state...</Typography>
        ) : devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No devices.</Typography>
        ) : (
          devices.map((d) => {
            const camKey = deviceCameraKey(d.id);
            const assignedToOther = Boolean(camKey && camKey !== selectedCameraKey);
            const otherCamLabel = assignedToOther
              ? (CAMERA_OPTIONS.find((c) => c.key === camKey)?.label || camKey)
              : null;
            const isChecked = !assignedToOther && selectedDeviceIds.includes(d.id);
            return (
              <FormControlLabel
                key={d.id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  ml: 0,
                  mr: 0,
                  mb: 0.5,
                  opacity: assignedToOther ? 0.55 : 1,
                  "& .MuiCheckbox-root": { pt: 0.25 },
                }}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleDevice(d.id)}
                    disabled={assignedToOther}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
                    {`${d.description || `Device ${d.id}`} (${Number(d.device_group) === 2 ? "Kiosk" : "Display"})`}
                    {assignedToOther && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: "warning.main" }}>
                        — already assigned to {otherCamLabel}
                      </Typography>
                    )}
                  </Typography>
                }
              />
            );
          })
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: 44, sm: 36 },
          },
        }}
      >
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outlined" color="error" onClick={disableAll} disabled={saving}>
          Disable All
        </Button>
      </Box>
    </Box>
  );
};

export default LiveCameras;
